import React from "react";
import { useNavigate } from "react-router-dom";
import logoFullDark from "../../assets/logo-dark.png";
import logoFullLight from "../../assets/logo-light.png";

type Kind = "privacidade" | "cookies";

const CONTENT: Record<Kind, { title: string; intro: string; sections: { h: string; p: string }[] }> = {
  privacidade: {
    title: "Política de Privacidade",
    intro:
      "A presente Política de Privacidade descreve como o LojaPilot recolhe, utiliza e protege os dados pessoais dos utilizadores da plataforma, em conformidade com o Regulamento Geral sobre a Proteção de Dados (RGPD — Regulamento (UE) 2016/679).",
    sections: [
      {
        h: "1. Responsável pelo tratamento",
        p: "O LojaPilot é a entidade responsável pelo tratamento dos dados pessoais recolhidos através da plataforma. Para questões relacionadas com privacidade, contacte-nos através dos canais indicados na página de contacto.",
      },
      {
        h: "2. Dados que recolhemos",
        p: "Recolhemos dados de registo (nome, email, dados da empresa), dados de configuração de loja, dados de utilização da plataforma e, no contexto das lojas geridas, dados de clientes e encomendas processados por conta dos lojistas, que atuam como responsáveis pelo tratamento desses dados.",
      },
      {
        h: "3. Finalidades",
        p: "Os dados são utilizados para fornecer e operar o serviço, autenticar utilizadores, processar pagamentos e subscrições, enviar comunicações de serviço e, com consentimento, comunicações comerciais.",
      },
      {
        h: "4. Partilha com terceiros",
        p: "Utilizamos subprocessadores estritamente necessários ao funcionamento do serviço: infraestrutura (Supabase), pagamentos (Stripe), notificações (OneSignal), email transacional (SMTP) e alojamento. Não vendemos dados pessoais a terceiros.",
      },
      {
        h: "5. Conservação e segurança",
        p: "Os dados são conservados apenas pelo período necessário às finalidades descritas ou exigido por lei, e protegidos por medidas técnicas e organizativas adequadas, incluindo cifragem em trânsito e controlo de acessos.",
      },
      {
        h: "6. Direitos do titular",
        p: "Nos termos do RGPD, pode exercer os direitos de acesso, retificação, apagamento, portabilidade, limitação e oposição ao tratamento, bem como apresentar reclamação à CNPD (Comissão Nacional de Proteção de Dados).",
      },
    ],
  },
  cookies: {
    title: "Política de Cookies",
    intro:
      "Esta Política de Cookies explica como o LojaPilot utiliza cookies e tecnologias semelhantes (incluindo armazenamento local do navegador) para funcionamento, preferências e análise.",
    sections: [
      {
        h: "1. O que são cookies",
        p: "Cookies são pequenos ficheiros de texto armazenados no seu dispositivo. Utilizamos também localStorage e sessionStorage, tecnologias equivalentes, para guardar estado da aplicação.",
      },
      {
        h: "2. Cookies estritamente necessários",
        p: "São indispensáveis ao funcionamento: sessão de autenticação, preferências de loja ativa, dados offline-first do gestor e funcionamento da aplicação instalada (PWA/service worker). Sem estes, o serviço não funciona.",
      },
      {
        h: "3. Cookies de preferências",
        p: "Guardamos escolhas de interface como tema (claro/escuro), loja selecionada e configurações locais de exibição.",
      },
      {
        h: "4. Cookies analíticos e de terceiros",
        p: "Podemos utilizar serviços de terceiros (Stripe, OneSignal) que definem os seus próprios identificadores técnicos para segurança, prevenção de fraude e entrega de notificações push.",
      },
      {
        h: "5. Gestão de cookies",
        p: "Pode gerir ou eliminar cookies nas definições do seu navegador. Tenha em atenção que bloquear cookies estritamente necessários pode impedir o funcionamento da plataforma.",
      },
      {
        h: "6. Alterações",
        p: "Esta política pode ser atualizada periodicamente. A versão em vigor é sempre a publicada nesta página.",
      },
    ],
  },
};

export function LegalPage({ kind }: { kind: Kind }) {
  const navigate = useNavigate();
  const theme = document.documentElement.classList.contains('dark') ? 'dark' : 'light';
  const c = CONTENT[kind];

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-100">
      <header className="border-b border-slate-200 dark:border-slate-800 px-4 py-4">
        <div className="max-w-screen-lg mx-auto flex items-center justify-between">
          <img
            src={theme === 'dark' ? logoFullLight : logoFullDark}
            alt="LojaPilot"
            className="h-10 object-contain cursor-pointer"
            onClick={() => navigate('/')}
          />
          <button
            onClick={() => navigate(-1 as never)}
            className="text-xs font-bold text-emerald-600 dark:text-emerald-400 hover:underline"
          >
            Voltar
          </button>
        </div>
      </header>
      <main className="max-w-screen-md mx-auto px-4 py-12">
        <h1 className="font-heading text-3xl font-extrabold tracking-[-0.02em] mb-6">{c.title}</h1>
        <p className="text-sm leading-relaxed text-slate-600 dark:text-slate-300 mb-10">{c.intro}</p>
        <div className="space-y-8">
          {c.sections.map((s) => (
            <section key={s.h}>
              <h2 className="text-sm font-bold uppercase tracking-wider text-slate-800 dark:text-slate-200 mb-2">
                {s.h}
              </h2>
              <p className="text-sm leading-relaxed text-slate-600 dark:text-slate-400">{s.p}</p>
            </section>
          ))}
        </div>
        <p className="mt-12 text-xs text-slate-400">Última atualização: {new Date().toLocaleDateString('pt-PT')}</p>
      </main>
    </div>
  );
}
